import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { revenueService } from "../services/revenueService";

const EMPTY_PROGRESS = { daily: 0, weekly: 0, monthly: 0, annual: 0, total: 0 };

export const useRevenue = () => {
  const { currentUser, isAdmin } = useAuth();
  const [globalTarget, setGlobalTarget] = useState(null);
  const [teamProgress, setTeamProgress] = useState([]);
  const [userObjective, setUserObjective] = useState(null);
  const [userProgress, setUserProgress] = useState(EMPTY_PROGRESS);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAdminData = useCallback(async () => {
    const [target, team] = await Promise.all([
      revenueService.getGlobalTarget(),
      revenueService.getTeamProgress(),
    ]);

    setGlobalTarget(target);
    setTeamProgress(team);
    setUserObjective(null);
    setUserProgress(EMPTY_PROGRESS);
    setObservations([]);
  }, []);

  const loadUserData = useCallback(async () => {
    if (!currentUser) return;
    const [target, objective, progress, latestObservations] = await Promise.all(
      [
        revenueService.getGlobalTarget(),
        revenueService.getObjectiveForUser(currentUser.uid),
        revenueService.getUserProgress(currentUser.uid),
        revenueService.getObservations(currentUser.uid, 20),
      ]
    );

    setGlobalTarget(target);
    setTeamProgress([]);
    setUserObjective(objective);
    setUserProgress(progress || EMPTY_PROGRESS);
    setObservations(latestObservations);
  }, [currentUser]);

  const loadData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        await loadAdminData();
      } else {
        await loadUserData();
      }
    } catch (err) {
      console.error("Erreur chargement données chiffre d'affaires:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin, loadAdminData, loadUserData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateGlobalTarget = useCallback(
    async (payload) => {
      if (!isAdmin) {
        throw new Error(
          "Seul un administrateur peut modifier le chiffre d'affaires global"
        );
      }
      if (!currentUser) {
        throw new Error("Utilisateur non authentifié");
      }

      await revenueService.upsertGlobalTarget(payload, currentUser.uid);
      await loadData();
    },
    [currentUser, isAdmin, loadData]
  );

  const updateObjectiveForUser = useCallback(
    async (userId, payload) => {
      if (!isAdmin) {
        throw new Error("Seul un administrateur peut fixer des objectifs");
      }
      if (!currentUser) {
        throw new Error("Utilisateur non authentifié");
      }

      await revenueService.setObjectiveForUser(
        userId,
        payload,
        currentUser.uid
      );
      await loadData();
    },
    [currentUser, isAdmin, loadData]
  );

  const addObservation = useCallback(
    async (userId, payload) => {
      if (!isAdmin) {
        throw new Error("Seul un administrateur peut ajouter une observation");
      }
      if (!currentUser) {
        throw new Error("Utilisateur non authentifié");
      }

      await revenueService.addObservation(userId, payload, currentUser.uid);
      await loadData();
    },
    [currentUser, isAdmin, loadData]
  );

  const latestObservation = useMemo(() => {
    if (!observations || observations.length === 0) return null;
    return observations[0];
  }, [observations]);

  return {
    loading,
    error,
    globalTarget,
    teamProgress,
    userObjective,
    userProgress,
    observations,
    latestObservation,
    refresh: loadData,
    updateGlobalTarget,
    updateObjectiveForUser,
    addObservation,
    isAdmin,
  };
};
